import React, {useCallback, useEffect, useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {InputNumber} from "primereact/inputnumber";
import {InputTextarea} from "primereact/inputtextarea";
import {Message} from "primereact/message";
import {Skeleton} from "primereact/skeleton";
import {Tag} from "primereact/tag";
import Avatar from "../../system/components/avatar";
import {useChatMessage} from "../../models/chat-message/use-chat-message";
import {useHome} from "../../models/home/use-home";
import useUserEventLog from "../../models/user-event-log/use-user-event-log";
import {setMessage} from "../../system/app-slice";
import {useTranslate} from "../../system/useTranslate";
import {TaskDateTime} from "../tasks/components/task-time-range";
import {getDashboardFocusTasks, getTaskPriority, isFocusTaskOverdue} from "./home-focus";
import {DEFAULT_FOCUS_DURATION_MINUTES} from "./focus-timer";
import HomePulse from "./home-pulse";
import {entityKey} from "./project-indicator";
import {useFocusSession} from "../../system/focus/focus-session-context";
import WeeklyFocusPanel from "./weekly-focus/weekly-focus-panel";
import FocusPickerDialog from "./weekly-focus/focus-picker-dialog";
import MyTasksSection from "./weekly-focus/my-tasks-section";
import {personalFocusApi} from "../../models/home/personal-focus-api";
import {SubscriptionHomeCard} from "../../system/components/subscription-status/subscription-status";

const compactActionIconStyle = {width: "2.25rem", height: "2.25rem"};

// what the server's focus codes mean to the member (zofino-strapi home.ts: add / reorder / remove a focus task)
const FOCUS_ERRORS = {
    FOCUS_LIMIT_REACHED: {key: "home.focus.limit-reached", severity: "warn", reload: true},
    FOCUS_VERSION_CONFLICT: {key: "home.focus.version-conflict", reload: true},
    ENTITY_NOT_FOUND: {key: "home.focus.task-unavailable", severity: "warn", reload: true},
    FOCUS_TASK_ASSIGNEE_REQUIRED: {key: "home.focus.assignee-required", severity: "warn", reload: true},
};

function Dashboard() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const translate = useTranslate();
    const messageApi = useChatMessage();
    const eventApi = useUserEventLog();
    const homeApi = useHome();
    const focus = useFocusSession();
    const user = useSelector((state) => state.auth.user);
    const membership = useSelector((state) => state.app.workspaceMembershipData);
    const workspace = membership?.workspace;
    const [homeData, setHomeData] = useState(null);
    const [homeLoading, setHomeLoading] = useState(false);
    const [homeError, setHomeError] = useState(null);
    const [focusSetupTask, setFocusSetupTask] = useState(null);
    const [focusDuration, setFocusDuration] = useState({hours: 0, minutes: DEFAULT_FOCUS_DURATION_MINUTES});
    const homeRequestRef = useRef(0);
    const focusPendingRef = useRef(false);
    const focusStartPendingRef = useRef(false);
    const attentionSectionRef = useRef(null);
    // «تمرکز این هفته» (docs/tasks/31-personal-focus.md): the range shown before a focus is chosen, and the picker
    const [focusRange, setFocusRange] = useState("week");
    const [pickerOpen, setPickerOpen] = useState(false);
    const [personalPending, setPersonalPending] = useState(false);

    const loadHome = useCallback(async () => {
        const requestId = ++homeRequestRef.current;
        const workspaceId = workspace?.documentId || workspace?.id;
        if (!workspaceId || !membership?.documentId || !user?.id) {
            setHomeData(null);
            setHomeLoading(false);
            setHomeError(null);
            return;
        }
        setHomeData(null);
        setHomeLoading(true);
        setHomeError(null);
        try {
            const result = await homeApi.getHome(workspaceId);
            if (requestId === homeRequestRef.current) setHomeData(result);
        } catch (requestError) {
            if (requestId === homeRequestRef.current) setHomeError(requestError);
        } finally {
            if (requestId === homeRequestRef.current) setHomeLoading(false);
        }
    }, [homeApi, membership?.documentId, user?.id, workspace?.documentId, workspace?.id]);

    useEffect(() => { loadHome(); }, [loadHome]);

    const personalFocusSection = homeData?.sections?.personalFocus;
    const personalFocus = personalFocusSection?.data?.focus || null;
    const actionRequiredSection = homeData?.sections?.actionRequired;
    const requiredActions = actionRequiredSection?.data?.items || [];
    const myTasksError = homeError || (["error", "permission_denied", "unavailable"].includes(personalFocusSection?.state) ? personalFocusSection?.error || {} : null);
    const actionsError = homeError || (["error", "permission_denied"].includes(actionRequiredSection?.state) ? actionRequiredSection?.error : null);
    const focusSnapshot = {...(homeData?.sections?.focus?.data || {limit: 3, version: 0, items: []}), currentSession: focus.session};
    const persistedFocusTasks = getDashboardFocusTasks(focusSnapshot.items, null);
    const focusTasks = getDashboardFocusTasks(focusSnapshot.items, focusSnapshot.currentSession);
    const focusTaskIds = focusTasks.map((task) => String(entityKey(task)));
    const workspaceId = workspace?.documentId || workspace?.id;

    const savePersonalFocus = async (input, message = "تمرکز شخصی تغییر کرد؛ کارهای محول‌شده به تو در این محدوده بالا آمدند.") => {
        if (!workspaceId || personalPending) return;
        setPersonalPending(true);
        try {
            const section = await personalFocusApi.save(workspaceId, input);
            setHomeData((current) => ({...(current || {}), sections: {...(current?.sections || {}), personalFocus: section}}));
            setPickerOpen(false);
            dispatch(setMessage({severity: "success", content: message}));
        } catch {
            dispatch(setMessage({severity: "error", content: "تمرکز ذخیره نشد. دوباره تلاش کن."}));
        } finally {
            setPersonalPending(false);
        }
    };

    const sameFocus = (rangeKind) => ({scopeType: personalFocus.scopeType, targetId: personalFocus.spotlight?.key || null, rangeKind});

    const changeFocusRange = (rangeKind) => {
        setFocusRange(rangeKind);
        if (personalFocus && !personalFocus.unavailable && rangeKind !== personalFocus.rangeKind) {
            savePersonalFocus(sameFocus(rangeKind), "بازه‌ی تمرکز از امروز تغییر کرد.");
        }
    };

    const applyFocusSnapshot = (snapshot) => {
        setHomeData((current) => ({
            ...(current || {}),
            sections: {
                ...(current?.sections || {}),
                focus: {
                    state: snapshot?.items?.length ? "ready" : "empty",
                    isCompleteDataset: true,
                    data: snapshot,
                    error: null,
                },
                ...(current?.sections?.pulse ? {
                    pulse: {
                        ...current.sections.pulse,
                        data: {...current.sections.pulse.data, focusCount: snapshot?.items?.length || 0},
                    },
                } : {}),
            },
        }));
    };

    const focusMutation = async (action) => {
        if (!workspaceId || focusPendingRef.current) return;
        focusPendingRef.current = true;
        try {
            applyFocusSnapshot(await action());
        } catch (requestError) {
            // each refusal says what to do; the card is reloaded whenever it may be out of date
            const known = FOCUS_ERRORS[requestError?.code];
            if (known?.reload) await loadHome();
            dispatch(setMessage({severity: known?.severity || "error", content: translate(known?.key || "home.focus.save-error")
                .replace("{{limit}}", String(requestError?.details?.limit || focusSnapshot.limit || 3))}));
        } finally {
            focusPendingRef.current = false;
        }
    };

    const addToFocus = async (task) => {
        const taskId = entityKey(task);
        if (!taskId || focusSnapshot.items?.length >= focusSnapshot.limit) return;
        await focusMutation(() => homeApi.addFocusItem(workspaceId, taskId, focusSnapshot.version));
    };

    const removeFocusTask = async (task) => {
        if (!task.__focusItemId) return;
        await focusMutation(() => homeApi.removeFocusItem(
            workspaceId,
            task.__focusItemId,
            focusSnapshot.version,
        ));
    };

    const moveFocusTask = async (task, offset) => {
        const from = persistedFocusTasks.findIndex((item) => String(entityKey(item)) === String(entityKey(task)));
        const to = from + offset;
        if (from < 0 || to < 0 || to >= persistedFocusTasks.length) return;
        const orderedIds = persistedFocusTasks.map((item) => item.__focusItemId);
        [orderedIds[from], orderedIds[to]] = [orderedIds[to], orderedIds[from]];
        await focusMutation(() => homeApi.reorderFocusItems(
            workspaceId,
            orderedIds,
            focusSnapshot.version,
        ));
    };

    const startFocus = async (task) => {
        const currentSession = focusSnapshot.currentSession;
        if (currentSession && String(entityKey(currentSession.task)) === String(entityKey(task))) {
            await focus.refresh();
            focus.openFocusMode?.();
            return;
        }
        setFocusDuration({hours: 0, minutes: DEFAULT_FOCUS_DURATION_MINUTES});
        setFocusSetupTask(task);
    };

    const confirmFocusStart = async () => {
        if (!focusSetupTask || focus.pending === "start" || focusStartPendingRef.current) return;
        const totalMinutes = (focusDuration.hours * 60) + focusDuration.minutes;
        if (totalMinutes < 5) return;
        focusStartPendingRef.current = true;
        try {
            const startedSession = await focus.start(focusSetupTask, totalMinutes * 60);
            if (!startedSession) return;
            setFocusSetupTask(null);
            focus.openFocusMode?.();
        } catch {
            dispatch(setMessage({severity: "error", content: translate("home.focus-mode.start-error")}));
        } finally {
            focusStartPendingRef.current = false;
        }
    };

    const respondToAction = async (item, responseText) => {
        const key = entityKey(item);
        const text = String(responseText || "").trim();
        if (!key || !text || item.eventCategory !== "mention" || item.requiresResponse !== true) {
            throw new Error("invalid-action-response");
        }

        try {
            if (item.payload?.chatDocumentId) {
                await messageApi.createChatMessage({
                    chat: item.payload.chatDocumentId,
                    messageContent: text,
                    parent: item.payload.messageDocumentId || undefined,
                });
            }
            const updated = await eventApi.updateUserEventLog(key, {
                responseText: text,
                responseState: "responded",
                isRead: true,
            });
            if (!updated) throw new Error("action-response-update-failed");
            setHomeData((current) => {
                const section = current?.sections?.actionRequired;
                if (!section) return current;
                const items = (section.data?.items || []).filter((candidate) => String(entityKey(candidate)) !== String(key));
                return {
                    ...current,
                    sections: {
                        ...current.sections,
                        actionRequired: {
                            ...section,
                            state: items.length ? "ready" : "empty",
                            data: {
                                ...section.data,
                                items,
                                meta: {
                                    ...(section.data?.meta || {}),
                                    totalCount: Math.max(0, Number(section.data?.meta?.totalCount || 1) - 1),
                                },
                            },
                        },
                    },
                };
            });
            dispatch(setMessage({severity: "success", content: translate("home.actions.response-success")}));
            return updated;
        } catch (requestError) {
            dispatch(setMessage({severity: "error", content: translate("home.actions.response-error")}));
            throw requestError;
        }
    };

    const displayName = user?.fullname || user?.firstname || translate("home.user-fallback");

    return (<>
        <main className="shabnam-font surface-ground min-h-full p-3 md:p-4 lg:p-5">
            <div className="w-full max-w-7xl mx-auto flex flex-column gap-4">
                <header className="flex flex-column sm:flex-row sm:align-items-center justify-content-between gap-3 px-1">
                    <div className="min-w-0">
                        <span className="text-xs text-primary font-bold">{translate("home.today-label")}</span>
                        <h1 className="text-2xl text-900 line-height-2 m-0 mt-2">{translate("home.greeting").replace("{{name}}", displayName)}</h1>
                        <p className="text-xs text-500 line-height-3 m-0 mt-1">{translate("home.header-description").replace("{{workspace}}", workspace?.name || translate("home.workspace-fallback"))}</p>
                    </div>
                    {/*<Avatar memberData={user} size="large" shape="circle" className="font-bold flex-shrink-0"/>*/}
                </header>

                <HomePulse focusCount={homeError ? null : homeData?.sections?.pulse?.data?.focusCount ?? null} attentionCount={homeError ? null : homeData?.sections?.pulse?.data?.attentionCount ?? null} actionCount={homeError ? null : homeData?.sections?.pulse?.data?.actionRequiredCount ?? null} loading={homeLoading} translate={translate}/>

                <SubscriptionHomeCard workspace={workspaceId}/>

                <FocusSelectionSection tasks={focusTasks} selectedCount={persistedFocusTasks.length} limit={focusSnapshot.limit || 3} currentSession={focusSnapshot.currentSession} translate={translate} onRemove={removeFocusTask} onMove={moveFocusTask} onStart={startFocus} onChooseNext={() => attentionSectionRef.current?.scrollIntoView({behavior: "smooth", block: "start"})}/>

                <WeeklyFocusPanel section={personalFocusSection} loading={homeLoading} range={focusRange} pending={personalPending} onRangeChange={changeFocusRange} onChoose={() => setPickerOpen(true)} onRenew={() => savePersonalFocus(sameFocus(personalFocus.rangeKind), "تمرکز برای بازه‌ی تازه تمدید شد.")}/>

                <MyTasksSection sectionRef={attentionSectionRef} section={personalFocusSection} loading={homeLoading} error={myTasksError} retry={loadHome} todayKeys={focusTaskIds} todayFull={persistedFocusTasks.length >= (focusSnapshot.limit || 3)} onAddToToday={addToFocus} onOpenTask={(task) => navigate({search: `?task=${encodeURIComponent(task.documentId || task.id)}`})} onFocusProject={(projectKey) => savePersonalFocus({scopeType: "project", targetId: projectKey, rangeKind: personalFocus?.rangeKind || focusRange})}/>

                <ActionRequiredSection items={requiredActions} loading={homeLoading} error={actionsError} retry={loadHome} translate={translate} onRespond={respondToAction} onViewAll={() => navigate("/inbox?tab=incoming")}/>

                <HomeSummaryStrip translate={translate} sections={homeData?.sections} loading={homeLoading} error={homeError}/>
            </div>
        </main>
        <FocusPickerDialog visible={pickerOpen} workspaceId={workspaceId} range={personalFocus && !personalFocus.unavailable ? personalFocus.rangeKind : focusRange} current={personalFocus && !personalFocus.unavailable ? personalFocus : null} pending={personalPending} onHide={() => !personalPending && setPickerOpen(false)} onApply={(input) => savePersonalFocus(input)}/>
        <FocusSetupDialog task={focusSetupTask} duration={focusDuration} pending={focus.pending === "start"} translate={translate} onDurationChange={setFocusDuration} onHide={() => { if (focus.pending !== "start") setFocusSetupTask(null); }} onConfirm={confirmFocusStart}/>
    </>);
}

function HomeSummaryStrip({translate, sections, loading, error}) {
    const meeting = sections?.nextMeeting;
    const capacity = sections?.capacity;
    const capacityMinutes = capacity?.state === "ready" ? capacity.data?.remainingMinutes : null;
    const items = [
        {key: "meeting", icon: "pi-clock", iconClass: "brand-secondary-muted", label: translate("home.meeting.title"), value: meeting?.state === "ready" ? meeting.data?.title : translate("home.meeting.unavailable"), meta: translate("home.summary.no-data")},
        {key: "capacity", icon: "pi-chart-line", iconClass: "bg-primary-50 text-primary", label: translate("home.capacity.remaining"), value: capacityMinutes === null ? translate("home.capacity.unavailable") : formatDuration(capacityMinutes, translate), meta: capacity?.state === "ready" ? `${capacity.data?.usedPercentage ?? 0}%` : translate("home.summary.no-estimate")},
    ];
    return <section data-home-section="summary" className="grid" aria-label={translate("home.summary.title")}>{items.map((item) => <div key={item.key} className="col-12 md:col-6"><article className="flex flex-wrap sm:flex-nowrap align-items-center gap-3 w-full h-full surface-card border-1 border-100 border-round-xl shadow-2 p-3"><span className={`w-3rem h-3rem border-round-xl flex align-items-center justify-content-center flex-shrink-0 ${item.iconClass}`}><i className={`pi ${item.icon}`} aria-hidden="true"/></span><span className="flex-1 min-w-0"><small className="block text-xs text-500 mb-1">{item.label}</small><strong className="block text-sm text-900 line-height-3 overflow-hidden text-overflow-ellipsis">{loading ? translate("home.pulse.description") : error ? translate("home.pulse.unavailable") : item.value}</strong></span><small className="text-xs text-500 flex-shrink-0">{item.meta}</small></article></div>)}</section>;
}

function formatDuration(value, translate) {
    const minutes = Math.max(0, Number(value) || 0);
    const hours = Math.floor(minutes / 60);
    const remainder = minutes % 60;
    return [
        hours ? `${new Intl.NumberFormat().format(hours)} ${translate("hours")}` : null,
        remainder || !hours ? `${new Intl.NumberFormat().format(remainder)} ${translate("minutes")}` : null,
    ].filter(Boolean).join(" ");
}

function FocusDurationField({inputId, label, value, min, max, step, translate, onChange}) {
    const update = (nextValue) => onChange(Math.max(min, Math.min(max, Number(nextValue) || 0)));
    return <div className="flex flex-column gap-2 min-w-0">
        <label htmlFor={inputId} className="text-xs text-700 font-bold">{label}</label>
        <div className="focus-duration-control flex align-items-stretch surface-card border-1 border-200 border-round-xl overflow-hidden">
            <Button type="button" text className="w-3rem h-3rem flex-shrink-0 border-noround" icon="pi pi-minus" aria-label={`${translate("home.focus-setup.decrease")} ${label}`} disabled={value <= min} onClick={() => update(value - step)}/>
            <InputNumber inputId={inputId} value={value} min={min} max={max} useGrouping={false} className="flex-1 min-w-0" inputClassName="w-full h-3rem text-center ltr border-none border-noround shadow-none" onValueChange={(event) => update(event.value)}/>
            <Button type="button" text className="w-3rem h-3rem flex-shrink-0 border-noround" icon="pi pi-plus" aria-label={`${translate("home.focus-setup.increase")} ${label}`} disabled={value >= max} onClick={() => update(value + step)}/>
        </div>
    </div>;
}

function FocusSetupDialog({task, duration, pending, translate, onDurationChange, onHide, onConfirm}) {
    const totalMinutes = (duration.hours * 60) + duration.minutes;
    return <Dialog visible={Boolean(task)} modal dismissableMask={!pending} closeOnEscape={!pending} draggable={false} header={translate("home.focus-setup.title")} className="rtl shabnam-font focus-setup-dialog w-11 sm:w-30rem border-round-xl shadow-2 overflow-hidden" contentClassName="p-0" onHide={onHide}>
        <div className="surface-card px-3 sm:px-4 pb-4 flex flex-column gap-4">
            <div className="surface-50 border-1 border-100 border-round-xl p-3">
                <small className="block text-xs text-500 mb-2">{translate("home.focus-setup.task")}</small>
                <strong className="block text-sm text-900 line-height-3">{task?.title}</strong>
            </div>
            <p className="text-xs text-500 line-height-3 m-0">{translate("home.focus-setup.description")}</p>
            <div className="grid">
                <div className="col-12 sm:col-6"><FocusDurationField inputId="focus-duration-hours" label={translate("home.focus-setup.hours")} value={duration.hours} min={0} max={8} step={1} translate={translate} onChange={(hours) => onDurationChange({...duration, hours})}/></div>
                <div className="col-12 sm:col-6"><FocusDurationField inputId="focus-duration-minutes" label={translate("home.focus-setup.minutes")} value={duration.minutes} min={0} max={59} step={5} translate={translate} onChange={(minutes) => onDurationChange({...duration, minutes})}/></div>
            </div>
            <div className="surface-100 border-round-xl p-3 flex align-items-center justify-content-between gap-3">
                <span className="text-xs text-500">{translate("home.focus-setup.total")}</span>
                <strong className="text-sm text-primary">{formatDuration(totalMinutes, translate)}</strong>
            </div>
            {totalMinutes < 5 && <small className="text-xs text-red-500">{translate("home.focus-setup.minimum")}</small>}
            <div className="flex flex-column-reverse sm:flex-row sm:justify-content-end gap-2 border-top-1 border-100 pt-3">
                <Button type="button" outlined severity="secondary" className="text-xs justify-content-center" label={translate("cancel")} disabled={pending} onClick={onHide}/>
                <Button type="button" className="text-xs justify-content-center" icon="pi pi-play" label={translate("home.focus-setup.start")} loading={pending} disabled={pending || totalMinutes < 5} onClick={onConfirm}/>
            </div>
        </div>
    </Dialog>;
}

function FocusSelectionSection({tasks, selectedCount, limit, currentSession, translate, onRemove, onMove, onStart, onChooseNext}) {
    return <section data-home-section="focus" className="home-focus-card surface-card border-round-xl shadow-2 overflow-hidden" aria-labelledby="home-focus-title">
        <header className="flex flex-column sm:flex-row sm:align-items-end justify-content-between gap-3 p-3 md:p-4">
            <div className="min-w-0">
                <small className="block text-xs text-primary font-bold mb-2">{translate("home.focus.eyebrow")}</small>
                <div className="flex flex-wrap align-items-center gap-2"><h2 id="home-focus-title" className="text-xl text-900 line-height-2 m-0">{translate("home.focus.title")}</h2><Tag severity={selectedCount === limit ? "success" : "secondary"} className="text-xs font-normal" value={`${selectedCount}/${limit}`}/></div>
                <p className="text-xs text-500 line-height-3 m-0 mt-2">{translate("home.focus.description")}</p>
            </div>
            {selectedCount < limit && <span className="align-self-start sm:align-self-end bg-primary-50 text-primary border-round-lg px-3 py-2 text-xs flex-shrink-0">{translate("home.focus.slots-remaining").replace("{{count}}", limit - selectedCount)} <i className="pi pi-arrow-down mr-1 text-xs" aria-hidden="true"/></span>}
        </header>
        <div className="border-top-1 border-100">
            {tasks.map((task) => <FocusSelectionTask key={entityKey(task)} task={task} currentSession={currentSession} total={tasks.length} translate={translate} onRemove={() => onRemove(task)} onMove={(offset) => onMove(task, offset)} onStart={() => onStart(task)}/>) }
            {selectedCount < limit && <div className="p-3 md:px-4"><button type="button" className="flex align-items-center gap-3 w-full p-3 border-1 border-dashed border-200 border-round-xl bg-primary-50 text-right cursor-pointer" onClick={onChooseNext}><span className="w-2rem h-2rem flex align-items-center justify-content-center border-round-lg bg-primary-100 text-primary flex-shrink-0"><i className="pi pi-plus text-sm" aria-hidden="true"/></span><span className="flex-1 min-w-0"><strong className="block text-sm text-900 line-height-2">{translate("home.focus.choose-next")}</strong><small className="block text-xs text-500 line-height-3 mt-1">{translate("home.focus.choose-next-description")}</small></span></button></div>}
        </div>
    </section>;
}

function FocusSelectionTask({task, currentSession, total, translate, onRemove, onMove, onStart}) {
    const project = task.projects?.[0];
    const priority = getTaskPriority(task);
    const overdue = isFocusTaskOverdue(task);
    const canRemove = task.__focusCapabilities?.canRemove !== false;
    const canReorder = task.__focusCapabilities?.canReorder !== false;
    const canStart = task.__focusCapabilities?.canStart !== false;
    const focusPosition = Number.isInteger(task.__focusPosition) ? task.__focusPosition : null;
    const focusTotal = Number.isInteger(task.__focusTotal) ? task.__focusTotal : total;
    const sessionTaskId = entityKey(currentSession?.task);
    const anotherSessionIsActive = sessionTaskId && String(sessionTaskId) !== String(entityKey(task));
    return <article className="flex flex-column md:flex-row md:align-items-center gap-3 p-3 md:px-4 border-bottom-1 border-100 min-h-7rem">
        <div className="flex align-items-start gap-3 flex-1 min-w-0">
            <span className="w-2rem h-2rem flex align-items-center justify-content-center border-round-lg bg-primary-50 text-primary text-xs font-bold flex-shrink-0">{task.__isActiveFocusTask && focusPosition === null ? <i className="pi pi-bolt" aria-hidden="true"/> : focusPosition + 1}</span>
            <span className="block w-2rem h-2rem border-circle border-1 border-300 surface-card flex-shrink-0" aria-hidden="true"/>
            <div className="flex-1 min-w-0">
                <div className="flex flex-wrap align-items-center gap-2"><strong className="text-sm text-900 line-height-2">{task.title || translate("home.focus.untitled")}</strong>{task.__isActiveFocusTask && <Tag severity="danger" icon="pi pi-bolt" className="gap-1 text-2xs font-normal" value={translate("home.focus.active-now")}/>} {priority && <Tag severity={["critical", "high"].includes(priority) ? "danger" : "warning"} className="text-2xs font-normal" value={translate(`home.focus.priority.${priority}`)}/>}</div>
                <div className="flex flex-wrap align-items-center gap-2 mt-2 text-xs text-500">{project && <><span>{project.title || project.name}</span><i className="pi pi-arrow-left text-2xs text-300" aria-hidden="true"/></>}<span>{translate(task.taskStatus || "not-selected")}</span></div>
                <div className="flex flex-wrap align-items-center gap-2 mt-2"><span className="text-xs text-500">{translate("home.focus.why-attention")}</span>{task.taskStatus === "blocked" && <Tag severity="danger" className="text-2xs font-normal" value={translate("home.attention.blocked")}/>} {overdue && <Tag severity="warning" className="text-2xs font-normal" value={translate("home.focus.overdue")}/>} {!overdue && task.dueDate && <Tag severity="info" className="text-2xs font-normal" value={translate("home.focus.due-today")}/>}</div>
            </div>
        </div>
        <div className="flex flex-column align-items-end justify-content-center gap-2 align-self-end md:align-self-center flex-shrink-0">
            {task.dueDate && <TaskDateTime value={task.dueDate} className="text-xs text-500"/>}
            <div className="flex align-items-center gap-2">
                <Button size="small" className="gap-1 text-xs px-3 py-2" icon="pi pi-play" label={translate("home.focus-mode.start")} disabled={!canStart || anotherSessionIsActive} onClick={onStart}/>
                <span className="flex align-items-center gap-1" role="group" aria-label={translate("home.focus.order-controls")}>
                    <Button outlined severity="secondary" size="small" className="w-2rem h-2rem p-0 border-round-lg" icon="pi pi-arrow-up" aria-label={translate("home.focus.move-up")} disabled={!canReorder || focusPosition === null || focusPosition === 0} onClick={() => onMove(-1)}/>
                    <Button outlined severity="secondary" size="small" className="w-2rem h-2rem p-0 border-round-lg" icon="pi pi-arrow-down" aria-label={translate("home.focus.move-down")} disabled={!canReorder || focusPosition === null || focusPosition === focusTotal - 1} onClick={() => onMove(1)}/>
                    <Button outlined severity="danger" size="small" className="w-2rem h-2rem p-0 border-round-lg" icon="pi pi-times" aria-label={translate("home.focus.remove-selection")} disabled={!canRemove} onClick={onRemove}/>
                </span>
            </div>
        </div>
    </article>;
}

function ActionRequiredSection({items, loading, error, retry, translate, onRespond, onViewAll}) {
    const [openKey, setOpenKey] = useState(null);
    const [drafts, setDrafts] = useState({});
    const [pendingKey, setPendingKey] = useState(null);
    const [failedKey, setFailedKey] = useState(null);
    const pendingResponseRef = useRef(null);

    const toggleResponse = (item) => {
        const key = String(entityKey(item));
        setFailedKey(null);
        setOpenKey((current) => current === key ? null : key);
    };

    const closeResponse = (item) => {
        const key = String(entityKey(item));
        setOpenKey(null);
        setFailedKey(null);
        setDrafts((current) => ({...current, [key]: ""}));
    };

    const submitResponse = async (event, item) => {
        event.preventDefault();
        const key = String(entityKey(item));
        const text = String(drafts[key] || "").trim();
        if (!text || pendingResponseRef.current) return;
        pendingResponseRef.current = key;
        setPendingKey(key);
        setFailedKey(null);
        try {
            await onRespond(item, text);
            setDrafts((current) => ({...current, [key]: ""}));
            setOpenKey(null);
        } catch {
            setFailedKey(key);
        } finally {
            pendingResponseRef.current = null;
            setPendingKey(null);
        }
    };

    return <section data-home-section="actions" className="home-actions-card surface-card border-round-xl shadow-2 overflow-hidden" aria-labelledby="home-actions-title">
        <header className="flex flex-column md:flex-row md:align-items-start justify-content-between gap-3 p-3 md:p-4 border-bottom-1 border-100"><div><small className="block text-xs brand-secondary-text font-bold mb-2">{translate("home.actions.eyebrow")}</small><div className="flex align-items-center gap-2"><h2 id="home-actions-title" className="text-xl text-900 line-height-2 m-0">{translate("home.actions.title")}</h2><Tag severity="secondary" value={items.length}/></div><p className="text-xs text-500 line-height-3 m-0 mt-2">{translate("home.actions.description")}</p></div>
            <Button text size="small" className="text-xs gap-2 px-2 py-2" icon="pi pi-arrow-left" iconPos="right" label={translate("home.actions.view-all")} onClick={onViewAll}/>
        </header>
        {loading ? <CompactSkeleton/> : error ? <PanelError message={translate("home.actions.load-error")} retry={retry} translate={translate}/> : items.length ? items.map((item) => {
            const key = String(entityKey(item));
            return <ActionRequiredItem
                key={key}
                item={item}
                expanded={openKey === key}
                response={drafts[key] || ""}
                pending={pendingKey === key}
                failed={failedKey === key}
                translate={translate}
                onToggle={() => toggleResponse(item)}
                onCancel={() => closeResponse(item)}
                onResponseChange={(value) => setDrafts((current) => ({...current, [key]: value}))}
                onSubmit={(event) => submitResponse(event, item)}
            />;
        }) : <PanelEmpty icon="pi pi-check-circle" title={translate("home.actions.empty-title")} description={translate("home.actions.empty-description")}/>}
    </section>;
}

function ActionRequiredItem({item, expanded, response, pending, failed, translate, onToggle, onCancel, onResponseChange, onSubmit}) {
    const actor = item.actor?.member;
    const actorName = actor?.fullname || [actor?.firstname, actor?.lastname].filter(Boolean).join(" ");
    const responseId = `home-action-response-${entityKey(item)}`;
    return <article className={`w-full border-bottom-1 border-100 p-3 md:px-4 md:py-3 min-h-6rem ${expanded ? "surface-50" : "surface-card"}`}>
        <div className="flex flex-column md:flex-row md:align-items-start gap-3">
            <span className="flex flex-1 align-items-start gap-3 min-w-0">
                {actor ? <Avatar memberData={actor} shape="circle" className="text-xs flex-shrink-0" style={compactActionIconStyle}/> : <span className="flex align-items-center justify-content-center border-round-lg brand-secondary-muted flex-shrink-0" style={compactActionIconStyle}><i className="pi pi-at text-sm" aria-hidden="true"/></span>}
                <span className="flex-1 min-w-0">
                    <span className="flex flex-wrap align-items-center gap-2">
                        <strong className="text-sm text-900 line-height-2">{item.title || translate("home.actions.mention")}</strong>
                        <Tag severity="warning" icon="pi pi-at" className="gap-1 text-2xs font-normal" value={translate("home.actions.waiting")}/>
                    </span>
                    <span className="flex flex-wrap align-items-center gap-2 text-xs text-500 mt-1">
                        {actorName && <strong className="text-700">{actorName}</strong>}
                        {item.sourceLabel && <span>{item.sourceLabel}</span>}
                        {item.contextLabel && <span>{item.contextLabel}</span>}
                        {item.createdAt && <TaskDateTime value={item.createdAt} className="text-xs text-500"/>}
                    </span>
                    {item.message && <p className="text-xs text-700 line-height-3 m-0 mt-2">{item.message}</p>}
                </span>
            </span>
            <span className="flex flex-wrap align-items-center gap-2 align-self-end md:align-self-start flex-shrink-0">
                <Tag severity="secondary" className="text-2xs font-normal" value={translate("home.actions.direct-mention")}/>
                <Button
                    outlined
                    severity="secondary"
                    size="small"
                    className="gap-1 text-xs px-3 py-2"
                    icon={expanded ? "pi pi-times" : "pi pi-reply"}
                    label={translate(expanded ? "home.actions.close" : "home.actions.reply")}
                    aria-expanded={expanded}
                    aria-controls={responseId}
                    onClick={onToggle}
                />
            </span>
        </div>
        {expanded && <form id={responseId} className="surface-card border-1 border-200 border-round-xl shadow-1 p-3 mt-3 md:mr-6rem" onSubmit={onSubmit}>
            <InputTextarea
                autoResize
                rows={3}
                value={response}
                disabled={pending}
                className="w-full text-xs line-height-3"
                style={{minHeight: "4.5rem"}}
                placeholder={translate("home.actions.reply-placeholder")}
                aria-label={translate("home.actions.reply-placeholder")}
                onChange={(event) => onResponseChange(event.target.value)}
            />
            {failed && <Message severity="error" className="w-full gap-2 text-xs mt-2" text={translate("home.actions.response-error")}/>}
            <div className="flex flex-wrap align-items-center gap-2 mt-2">
                <Button type="submit" size="small" className="gap-1 text-xs px-3 py-2" icon="pi pi-send" label={translate("home.actions.send-response")} loading={pending} disabled={pending || !String(response || "").trim()}/>
                <Button type="button" size="small" outlined severity="secondary" className="text-xs px-3 py-2" label={translate("home.actions.cancel")} disabled={pending} onClick={onCancel}/>
            </div>
        </form>}
    </article>;
}

function PanelEmpty({icon, title, description, action}) {
    return <div className="flex flex-column align-items-center justify-content-center text-center gap-2 p-4 min-h-12rem"><i className={`${icon} text-3xl text-400`}/><strong className="text-sm text-900">{title}</strong><span className="text-xs text-500 line-height-3">{description}</span>{action}</div>;
}

function PanelError({message, retry, translate}) {
    return <div className="flex flex-column align-items-center gap-3 p-3"><Message severity="error" className="w-full gap-2 text-xs" text={message}/><Button outlined className="gap-2 text-xs" icon="pi pi-refresh" label={translate("home.common.retry")} onClick={retry}/></div>;
}

function CompactSkeleton() {
    return <div>{[1, 2].map((item) => <div key={item} className="flex align-items-center gap-3 p-3 border-bottom-1 border-100"><Skeleton shape="circle" size="3rem"/><div className="flex-1"><Skeleton width="65%" height="1rem" className="mb-2"/><Skeleton width="40%" height=".75rem"/></div></div>)}</div>;
}

export default Dashboard;
